import React, { useState, useEffect } from 'react';
import {
  Upload, MessageSquare, Database, ArrowLeft, AlertCircle, Check, Flag, ScanSearch,
  Sparkles, RefreshCw, FileText, ChevronDown, ChevronUp, CheckCircle, HelpCircle,
  Edit3, Layers, Tag, X, ShieldAlert, Wrench, Search, CornerDownRight, PlusCircle, Eye, ExternalLink
} from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || (typeof window !== 'undefined' && window.location.hostname === 'localhost' ? 'http://localhost:3001' : 'https://support-ai-gctw.onrender.com');

const GAP_TYPE_CONFIG = {
  MISSING_SECTION: {
    label: 'Missing Section',
    color: '#f59e0b',
    bg: 'rgba(245, 158, 11, 0.12)',
    border: 'rgba(245, 158, 11, 0.3)',
    icon: Layers,
    description: 'Document exists but is missing this specific procedure or section.'
  },
  MISSING_DOCUMENT: {
    label: 'Missing Document',
    color: '#a855f7',
    bg: 'rgba(168, 85, 247, 0.12)',
    border: 'rgba(168, 85, 247, 0.3)',
    icon: FileText,
    description: 'Entirely new topic not covered in the knowledge base.'
  },
  TERMINOLOGY_GAP: {
    label: 'Terminology Gap',
    color: '#3b82f6',
    bg: 'rgba(59, 130, 246, 0.12)',
    border: 'rgba(59, 130, 246, 0.3)',
    icon: Tag,
    description: 'Information exists under different naming or terms.'
  },
  DEPTH_GAP: {
    label: 'Depth / How-To Gap',
    color: '#06b6d4',
    bg: 'rgba(6, 182, 212, 0.12)',
    border: 'rgba(6, 182, 212, 0.3)',
    icon: ScanSearch,
    description: 'Topic is introduced but lacks actionable step-by-step detail.'
  },
  EDGE_CASE: {
    label: 'Edge Case Scenario',
    color: '#f97316',
    bg: 'rgba(249, 115, 22, 0.12)',
    border: 'rgba(249, 115, 22, 0.3)',
    icon: AlertCircle,
    description: 'Standard flow is documented, but this specific scenario is missing.'
  },
  PERMISSIONS_GAP: {
    label: 'Permissions / Access',
    color: '#f43f5e',
    bg: 'rgba(244, 63, 94, 0.12)',
    border: 'rgba(244, 63, 94, 0.3)',
    icon: ShieldAlert,
    description: 'Feature behavior depends on user role or access prerequisites.'
  },
  RETRIEVAL_FAILURE: {
    label: 'Retrieval Failure',
    color: '#ef4444',
    bg: 'rgba(239, 68, 68, 0.12)',
    border: 'rgba(239, 68, 68, 0.3)',
    icon: Search,
    description: 'Info appears to exist, but retrieval missed or fetched wrong docs.'
  },
  INCOMPLETE_INFORMATION: {
    label: 'Incomplete Information',
    color: '#eab308',
    bg: 'rgba(234, 179, 8, 0.12)',
    border: 'rgba(234, 179, 8, 0.3)',
    icon: HelpCircle,
    description: 'Directionally related docs exist but lack crucial parameters.'
  },
  ANSWER_GENERATION_FAILURE: {
    label: 'Answer Generation Issue',
    color: '#8b5cf6',
    bg: 'rgba(139, 92, 246, 0.12)',
    border: 'rgba(139, 92, 246, 0.3)',
    icon: Wrench,
    description: 'Retrieved docs had info, but answer formulation failed.'
  },
  WRONG_SOURCE: {
    label: 'Wrong Source Document',
    color: '#f43f5e',
    bg: 'rgba(244, 63, 94, 0.12)',
    border: 'rgba(244, 63, 94, 0.3)',
    icon: FileText,
    description: 'Retrieved or relied on an incorrect/unrelated source document.'
  },
  STALE_CONTENT: {
    label: 'Stale / Outdated Content',
    color: '#fb923c',
    bg: 'rgba(251, 146, 60, 0.12)',
    border: 'rgba(251, 146, 60, 0.3)',
    icon: RefreshCw,
    description: 'Knowledge base documentation is outdated or no longer accurate.'
  },
  HALLUCINATION: {
    label: 'Hallucination / Inaccuracy',
    color: '#ef4444',
    bg: 'rgba(239, 68, 68, 0.12)',
    border: 'rgba(239, 68, 68, 0.3)',
    icon: AlertCircle,
    description: 'Generated answer stated details not grounded in documentation.'
  },
  POOR_PHRASING: {
    label: 'Poor Phrasing / Confusing',
    color: '#a855f7',
    bg: 'rgba(168, 85, 247, 0.12)',
    border: 'rgba(168, 85, 247, 0.3)',
    icon: Sparkles,
    description: 'Answer was technically correct but confusing or poorly structured.'
  },
  FLAGGED_RESPONSE: {
    label: 'Reported Issue',
    color: '#ef4444',
    bg: 'rgba(239, 68, 68, 0.12)',
    border: 'rgba(239, 68, 68, 0.3)',
    icon: Flag,
    description: 'A teammate reported an issue with this specific response.'
  },
  OTHER: {
    label: 'Other Gap',
    color: '#71717a',
    bg: 'rgba(113, 113, 122, 0.12)',
    border: 'rgba(113, 113, 122, 0.3)',
    icon: HelpCircle,
    description: 'Specific knowledge or system issue.'
  },
  MANUAL_REVIEW: {
    label: 'Manual Review Required',
    color: '#71717a',
    bg: 'rgba(113, 113, 122, 0.12)',
    border: 'rgba(113, 113, 122, 0.3)',
    icon: AlertCircle,
    description: 'Automated analysis requires manual admin inspection.'
  },
};

export default function AdminDashboard({ onBackToChat }) {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [documents, setDocuments] = useState([]);
  const [unansweredQuestions, setUnansweredQuestions] = useState([]);

  const [loading, setLoading] = useState(false);
  const [uploadFilename, setUploadFilename] = useState('');
  const [sourceUrl, setSourceUrl] = useState(''); // NEW state
  const [uploadContent, setUploadContent] = useState('');
  const [uploadStatus, setUploadStatus] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [answeringQuestion, setAnsweringQuestion] = useState(null);
  const [answerText, setAnswerText] = useState('');
  const [answerFilename, setAnswerFilename] = useState('');
  const [cleanQuestion, setCleanQuestion] = useState(''); // NEW for V2
  const [backendReady, setBackendReady] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [unansweredFilter, setUnansweredFilter] = useState('all'); // 'all' | 'unanswered' | 'flagged' (Phase 8: Unified tab)

  // NEW: Editing state
  const [editingDoc, setEditingDoc] = useState(null); // { filename, content, sourceUrl }
  const [isEditing, setIsEditing] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState(new Set());
  const [groupedDocuments, setGroupedDocuments] = useState([]); // [{ filename, count, lastModified, sourceUrl }]
  // NEW: Tags state
  const [uploadTags, setUploadTags] = useState('');
  const [availableTags, setAvailableTags] = useState([]);
  const [filterTag, setFilterTag] = useState('');
  const [editingTags, setEditingTags] = useState('');
  const [showTagEdit, setShowTagEdit] = useState(false);
  const [tagEditFilename, setTagEditFilename] = useState('');
  const [expandedItems, setExpandedItems] = useState(new Set()); // NEW: Track expanded cards by ID
  const [selectedTargetDocs, setSelectedTargetDocs] = useState({}); // q.id -> chosen doc filename
  const [targetDocSearchQuery, setTargetDocSearchQuery] = useState({}); // q.id -> search string
  const [showDocPicker, setShowDocPicker] = useState({}); // q.id -> boolean



  // Phase 3: Document Ingestion & Resolution States
  const [resolutionMode, setResolutionMode] = useState('UPDATE_EXISTING'); // 'UPDATE_EXISTING' | 'CREATE_NEW'
  const [targetDocFilename, setTargetDocFilename] = useState('');
  const [isFetchingDoc, setIsFetchingDoc] = useState(false);

  // Session Viewer Modal States
  const [sessionModalOpen, setSessionModalOpen] = useState(false);
  const [sessionModalData, setSessionModalData] = useState(null);
  const [loadingSessionModal, setLoadingSessionModal] = useState(false);

  const openSessionModal = async (sessionOrLogId) => {
    if (!sessionOrLogId) return;
    setSessionModalOpen(true);
    setLoadingSessionModal(true);
    setSessionModalData(null);

    // 1. Check if session already exists in analyticsData
    if (analyticsData?.recent_sessions) {
      const found = analyticsData.recent_sessions.find(s => s.session_id === sessionOrLogId || s.messages?.some(m => m.id === sessionOrLogId));
      if (found) {
        setSessionModalData(found);
        setLoadingSessionModal(false);
        return;
      }
    }

    // 2. Fetch directly from backend
    try {
      const res = await fetch(`${API_URL}/chat-logs/session/${encodeURIComponent(sessionOrLogId)}`);
      if (res.ok) {
        const data = await res.json();
        setSessionModalData(data);
      } else {
        setSessionModalData({ session_id: sessionOrLogId, messages: [] });
      }
    } catch (err) {
      console.error('Failed to fetch session for modal:', err);
      setSessionModalData({ session_id: sessionOrLogId, messages: [] });
    } finally {
      setLoadingSessionModal(false);
    }
  };

  const loadExistingDoc = async (baseFilename, draftToAdd = null) => {
    if (!baseFilename) return;
    setIsFetchingDoc(true);
    try {
      const res = await fetch(`${API_URL}/documents/group/${encodeURIComponent(baseFilename)}`);
      if (res.ok) {
        const data = await res.json();
        const content = data.content || '';
        if (draftToAdd && draftToAdd.trim()) {
          // Cleanly append AI draft structure at the end of the full doc
          const combined = content ? `${content.trim()}\n\n---\n\n### Suggested Addition / Update\n${draftToAdd.trim()}` : draftToAdd;
          setAnswerText(combined);
        } else {
          setAnswerText(content);
        }
      } else {
        if (draftToAdd) setAnswerText(draftToAdd);
      }
    } catch (e) {
      console.error('Failed to load existing document content:', e);
    } finally {
      setIsFetchingDoc(false);
    }
  };

  const toggleExpanded = (id) => {
    setExpandedItems(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const lastActivityRef = React.useRef(Date.now());
  const inactivityCheckRef = React.useRef(null);

  const fetchTags = async () => {
    try {
      const res = await fetch(`${API_URL}/tags`);
      const data = await res.json();
      setAvailableTags(data.tags || []);
    } catch (err) {
      console.error('Failed to fetch tags:', err);
    }
  };

  const checkBackend = async () => {
    try {
      const res = await fetch(`${API_URL}/`);
      if (res.ok) {
        setBackendReady(true);
        setIsRefreshing(false);
      }
    } catch (err) {
      setTimeout(checkBackend, 2000);
    }
  };

  const handleActivity = () => {
    const now = Date.now();
    const timeSinceLastActivity = now - lastActivityRef.current;

    if (timeSinceLastActivity > 10 * 60 * 1000) {
      setIsRefreshing(true);
      checkBackend();
    }

    lastActivityRef.current = now;
  };

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    checkBackend();

    const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];
    events.forEach(event => {
      window.addEventListener(event, handleActivity);
    });

    inactivityCheckRef.current = setInterval(() => {
      const timeSinceLastActivity = Date.now() - lastActivityRef.current;
      if (timeSinceLastActivity > 10 * 60 * 1000 && !isRefreshing) {
        console.log('Long inactivity detected, backend may be sleeping');
      }
    }, 60000);

    return () => {
      events.forEach(event => {
        window.removeEventListener(event, handleActivity);
      });
      if (inactivityCheckRef.current) {
        clearInterval(inactivityCheckRef.current);
      }
    };
  }, []);
  /* eslint-enable react-hooks/exhaustive-deps */

  // Deep-link support: Auto-open specific unanswered question if ?tab=unanswered&id=123 in URL
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      const urlTab = params.get('tab');
      const urlId = params.get('id') || params.get('unanswered_id');
      if (urlTab === 'unanswered' || urlId) {
        setActiveTab('unanswered');
        if (urlId) {
          const numId = parseInt(urlId, 10);
          const targetId = isNaN(numId) ? urlId : numId;
          setExpandedItems(prev => new Set([...prev, targetId, String(targetId), Number(targetId)]));

          // Auto-scroll and highlight as soon as the questions are rendered
          let attempts = 0;
          const pollInterval = setInterval(() => {
            attempts++;
            const el = document.getElementById(`unanswered-card-${targetId}`) || document.getElementById(`unanswered-card-${String(targetId)}`);
            if (el) {
              clearInterval(pollInterval);
              el.scrollIntoView({ behavior: 'smooth', block: 'center' });
              el.style.boxShadow = '0 0 28px rgba(34, 197, 94, 0.6)';
              el.style.borderColor = 'rgba(34, 197, 94, 0.8)';
              setTimeout(() => {
                el.style.boxShadow = '0 8px 32px rgba(0, 0, 0, 0.25)';
                el.style.borderColor = 'rgba(255, 255, 255, 0.08)';
              }, 3500);
            }
            if (attempts > 30) {
              clearInterval(pollInterval);
            }
          }, 150);
        }
      }
    }
  }, [unansweredQuestions.length]);

  // NEW: Dashboard State
  const [analyticsData, setAnalyticsData] = useState(null);
  const [loadingAnalytics, setLoadingAnalytics] = useState(false);

  // Fetch analytics data
  const fetchAnalytics = async () => {
    setLoadingAnalytics(true);
    try {
      const res = await fetch(`${API_URL}/analytics`);
      const data = await res.json();
      setAnalyticsData(data);
    } catch (err) {
      console.error('Failed to fetch analytics:', err);
    } finally {
      setLoadingAnalytics(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'dashboard') {
      fetchAnalytics();
    }
  }, [activeTab]);

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!uploadFilename || !uploadContent) {
      alert('Please provide filename and content');
      return;
    }

    setLoading(true);
    setUploadStatus('Uploading...');

    try {
      // V2 is now the default upload path
      const res = await fetch(`${API_URL}/upload-v2`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          filename: uploadFilename,
          content: uploadContent,
          sourceUrl: sourceUrl,
          tags: uploadTags.split(',').map(t => t.trim()).filter(Boolean)
        }),
      });
      const data = await res.json();

      if (data.success) {
        setUploadStatus(`✅ Uploaded! Created ${data.chunks_created || 1} chunks`);
        setUploadFilename('');
        setSourceUrl('');
        setUploadTags(''); // Reset tags
        setUploadContent('');
        fetchDocuments();
      } else {
        setUploadStatus(`❌ ${data.error}`);
      }
    } catch (err) {
      setUploadStatus('❌ Upload failed: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAnswerQuestion = async (questionId) => {
    if (!answerText.trim()) {
      alert('Please provide knowledge content');
      return;
    }

    const finalCleanQuestion = cleanQuestion.trim() || unansweredQuestions.find(q => q.id === questionId)?.question;
    let finalDocName = answerFilename.trim() || finalCleanQuestion || `Guide ${questionId}`;
    // Strip trailing .txt and clean up
    finalDocName = finalDocName.replace(/\.txt$/i, '').trim();

    let finalMerged = answerText;

    if (resolutionMode === 'UPDATE_EXISTING') {
      finalDocName = targetDocFilename || answerFilename;
      finalMerged = answerText; // The textarea holds the live, full in-place edited document
    }

    setLoading(true);

    try {
      const res = await fetch(`${API_URL}/unanswered/${questionId}/answer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          mode: resolutionMode,
          answer: answerText,
          cleanQuestion: finalCleanQuestion,
          filename: finalDocName,
          targetDocument: finalDocName,
          mergedContent: finalMerged,
          adminUser: "Admin"
        }),
      });

      const data = await res.json();

      if (data.success) {
        setAnsweringQuestion(null);
        setAnswerText('');
        setCleanQuestion('');
        setAnswerFilename('');
        fetchUnanswered();
        fetchDocuments();
        alert(`✅ ${data.message || 'Knowledge successfully ingested!'}`);
      } else {
        alert('❌ Failed to save: ' + (data.error || 'Unknown error'));
      }
    } catch (err) {
      console.error('Failed to answer question:', err);
      alert('❌ Failed to save knowledge');
    } finally {
      setLoading(false);
    }
  };


  const parseGapAnalysis = (q) => {
    if (!q || !q.context) return null;
    try {
      const parsed = JSON.parse(q.context);
      if (parsed && typeof parsed === 'object' && (parsed.gap_type || parsed.original_question)) {
        return parsed;
      }
    } catch (e) {}
    return null;
  };

  const handleDismissQuestion = async (questionId) => {
    // Optimistically remove from view immediately
    setUnansweredQuestions(prev => prev.filter(q => q.id !== questionId));
    setExpandedItems(prev => {
      const next = new Set(prev);
      next.delete(questionId);
      next.delete(String(questionId));
      next.delete(Number(questionId));
      return next;
    });

    try {
      const res = await fetch(`${API_URL}/unanswered/${questionId}/dismiss`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: 'Dismissed by admin' }),
      });
      const data = await res.json();
      if (!data.success) {
        alert('❌ Failed to dismiss: ' + (data.error || 'Unknown error'));
        fetchUnanswered();
      }
    } catch (err) {
      console.error('Failed to dismiss:', err);
      alert('❌ Failed to dismiss');
      fetchUnanswered();
    }
  };

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    if (typeof window !== 'undefined') {
      const url = new URL(window.location.href);
      url.searchParams.set('tab', tabId);
      url.searchParams.delete('id');
      url.searchParams.delete('unanswered_id');
      window.history.replaceState({}, '', url.toString());
    }
  };

  const handleBackToChatClick = () => {
    if (typeof window !== 'undefined') {
      window.history.replaceState({}, '', window.location.pathname);
    }
    if (onBackToChat) onBackToChat();
  };

  const fetchDocuments = async () => {
    try {
      const res = await fetch(`${API_URL}/documents`);
      const data = await res.json();
      setDocuments(data.documents || []);

      // Group documents
      const groups = {};
      (data.documents || []).forEach(doc => {
        // Strip (chunk X/Y) suffix
        const baseName = doc.filename.replace(/\s*\(chunk \d+\/\d+\)$/, '');
        if (!groups[baseName]) {
          groups[baseName] = {
            filename: baseName,
            count: 0,
            ids: [],
            lastModified: doc.created_at,
            sourceUrl: doc.source_url,
            tags: new Set(doc.tags || []),

          };
        }
        groups[baseName].count++;
        groups[baseName].ids.push(doc.id);
        // Keep the most recent created_at
        if (new Date(doc.created_at) > new Date(groups[baseName].lastModified)) {
          groups[baseName].lastModified = doc.created_at;
        }
        // Collect all tags
        if (doc.tags && Array.isArray(doc.tags)) {
          doc.tags.forEach(tag => groups[baseName].tags.add(tag));
        }

      });
      setGroupedDocuments(Object.values(groups).map(g => ({
        ...g,
        tags: [...g.tags].sort(),

      })).sort((a, b) => new Date(b.lastModified) - new Date(a.lastModified)));

    } catch (err) {
      console.error('Failed to fetch documents:', err);
    }
  };



  const fetchUnanswered = async () => {
    try {
      const res = await fetch(`${API_URL}/unanswered`);
      const data = await res.json();
      setUnansweredQuestions(data.questions || []);
    } catch (err) {
      console.error('Failed to fetch unanswered:', err);
    }
  };

  useEffect(() => {
    if (backendReady) {
      fetchDocuments();
      fetchUnanswered();
      fetchTags();
    }
  }, [backendReady]);

  if (!backendReady || isRefreshing) {
    return (
      <div style={{
        minHeight: '100vh',
        backgroundColor: '#09090b',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
        gap: '24px',
        padding: '16px',
      }}>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
        <div style={{
          width: '60px',
          height: '60px',
          border: '4px solid #27272a',
          borderTop: '4px solid #8b5cf6',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
        }} />
        <p style={{ color: '#a1a1aa', fontSize: '14px', textAlign: 'center' }}>
          {isRefreshing ? 'Reconnecting to backend...' : 'Waking up the backend...'}
        </p>
      </div>
    );
  }

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this document?')) return;

    try {
      await fetch(`${API_URL}/documents/${id}`, { method: 'DELETE' });
      fetchDocuments();
    } catch (err) {
      console.error('Failed to delete:', err);
    }
  };

  const toggleGroup = (filename) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(filename)) {
        next.delete(filename);
      } else {
        next.add(filename);
      }
      return next;
    });
  };

  const handleEdit = async (baseFilename, currentSourceUrl) => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/documents/group/${encodeURIComponent(baseFilename)}`);
      const data = await res.json();

      if (data.error) throw new Error(data.error);

      setEditingDoc({
        originalFilename: baseFilename,
        content: data.content,
        sourceUrl: data.sourceUrl || currentSourceUrl || '',
        skipConversion: true // Default to just editing text
      });
      setIsEditing(true);
    } catch (err) {
      alert('Failed to load document content: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveEdit = async () => {
    if (!editingDoc.content.trim()) {
      alert('Content cannot be empty');
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/documents/group/${encodeURIComponent(editingDoc.originalFilename)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          content: editingDoc.content,
          sourceUrl: editingDoc.sourceUrl,
          skipConversion: editingDoc.skipConversion
        }),
      });

      const data = await res.json();
      if (data.error) throw new Error(data.error);

      alert('✅ Document updated successfully!');
      setIsEditing(false);
      setEditingDoc(null);
      fetchDocuments();
    } catch (err) {
      alert('Failed to update document: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateTags = async () => {
    if (!tagEditFilename) return;

    setLoading(true);
    try {
      const tagsArray = editingTags.split(',').map(t => t.trim()).filter(Boolean);
      const res = await fetch(`${API_URL}/documents/group/${encodeURIComponent(tagEditFilename)}/tags`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tags: tagsArray }),
      });

      const data = await res.json();
      if (data.error) throw new Error(data.error);

      alert('✅ Tags updated!');
      setShowTagEdit(false);
      setTagEditFilename('');
      setEditingTags('');
      fetchDocuments();
      fetchTags();
    } catch (err) {
      alert('Failed to update tags: ' + err.message);
    } finally {
      setLoading(false);
    }
  };



  const pendingCount = unansweredQuestions.filter(q => q.status === 'pending').length;

  const tabs = [
    { id: 'dashboard', label: 'Dashboard', icon: Database },
    { id: 'upload', label: 'Upload', icon: Upload },
    { id: 'knowledge', label: 'Knowledge', icon: Database },
    { id: 'unanswered', label: 'Unanswered', icon: AlertCircle, badge: pendingCount },
  ];

  const renderDashboard = () => (
    <div className="dashboard-container" style={{ animation: 'fadeIn 0.3s ease-out', paddingRight: '20px' }}>
      <h2 style={{ fontSize: '24px', fontWeight: '600', marginBottom: '24px', display: 'flex', alignItems: 'center', gap: '10px' }}>
        <div style={{ background: 'linear-gradient(135deg, #3b82f6, #8b5cf6)', padding: '8px', borderRadius: '10px' }}>
          <Database size={20} color="#fff" />
        </div>
        Analytics Dashboard
      </h2>

      {loadingAnalytics ? (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '20px' }}>
          {[1, 2, 3, 4].map(i => (
            <div key={i} style={{ height: '120px', background: 'rgba(39, 39, 42, 0.4)', borderRadius: '16px', animation: 'pulse 1.5s infinite' }} />
          ))}
        </div>
      ) : (
        <>
          {/* Stat Cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '20px', marginBottom: '32px' }}>
            <StatCard
              title="Total Documents"
              value={analyticsData?.total_documents || 0}
              icon={<Database size={20} color="#a855f7" />}
              color="rgba(168, 85, 247, 0.1)"
            />
            <StatCard
              title="Questions Today"
              value={analyticsData?.questions_today || 0}
              icon={<MessageSquare size={20} color="#3b82f6" />}
              color="rgba(59, 130, 246, 0.1)"
            />
            <StatCard
              title="Avg. Confidence"
              value={`${analyticsData?.avg_confidence || 0}%`}
              icon={<Check size={20} color="#10b981" />}
              color="rgba(16, 185, 129, 0.1)"
            />
          </div>

          <div style={{ marginTop: '24px' }}>
            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '20px', color: '#e4e4e7', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <MessageSquare size={18} /> Chat History (Last 3 Days)
            </h3>
            
            {analyticsData?.recent_sessions?.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                {analyticsData.recent_sessions.map((session, i) => (
                  <div key={i} style={{ padding: '24px', background: 'rgba(24, 24, 27, 0.6)', borderRadius: '20px', border: '1px solid rgba(255, 255, 255, 0.05)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px', borderBottom: '1px solid rgba(255, 255, 255, 0.05)', paddingBottom: '12px' }}>
                      <span style={{ color: '#a1a1aa', fontSize: '14px', fontWeight: '500' }}>
                        Session {session.session_id ? session.session_id.substring(0, 8) : 'Unknown'}
                      </span>
                      <span style={{ color: '#71717a', fontSize: '14px' }}>
                        {new Date(session.startTime).toLocaleString()}
                      </span>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                      {session.messages.map((msg, j) => (
                        <div key={j} style={{ display: 'flex', flexDirection: 'column', gap: '12px', background: 'rgba(39, 39, 42, 0.3)', padding: '16px', borderRadius: '12px' }}>
                          <div style={{ display: 'flex', gap: '12px' }}>
                            <div style={{ background: '#3b82f6', width: '24px', height: '24px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: '2px' }}>
                              <span style={{ color: '#fff', fontSize: '12px', fontWeight: 'bold' }}>Q</span>
                            </div>
                            <p style={{ margin: 0, color: '#fafafa', fontSize: '15px', lineHeight: '1.5' }}>{msg.question}</p>
                          </div>
                          
                          <div style={{ display: 'flex', gap: '12px' }}>
                            <div style={{ background: '#8b5cf6', width: '24px', height: '24px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: '2px' }}>
                              <span style={{ color: '#fff', fontSize: '12px', fontWeight: 'bold' }}>A</span>
                            </div>
                            <div style={{ flex: 1 }}>
                              <div style={{ color: '#d4d4d8', fontSize: '14px', lineHeight: '1.6', marginBottom: '12px', whiteSpace: 'pre-wrap' }}>
                                {msg.answer}
                              </div>
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center', background: 'rgba(0,0,0,0.2)', padding: '12px', borderRadius: '8px' }}>
                                <Badge type={msg.retrieval_confidence} />
                                <span style={{ color: '#71717a', fontSize: '12px' }}>Sim: {Number(msg.top_similarity).toFixed(3)}</span>
                                {msg.sources && msg.sources.length > 0 && (
                                  <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginLeft: '8px', borderLeft: '1px solid rgba(255,255,255,0.1)', paddingLeft: '12px' }}>
                                    {msg.sources.map((s, idx) => {
                                      const sName = typeof s === 'string' ? (s.startsWith('{') ? JSON.parse(s).name : s) : (s?.name || '');
                                      if (!sName) return null;
                                      return (
                                        <span key={idx} style={{ background: 'rgba(139, 92, 246, 0.1)', border: '1px solid rgba(139, 92, 246, 0.2)', color: '#a78bfa', fontSize: '11px', padding: '2px 8px', borderRadius: '12px' }}>
                                          {sName}
                                        </span>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ padding: '40px', textAlign: 'center', background: 'rgba(24, 24, 27, 0.6)', borderRadius: '20px', border: '1px solid rgba(255, 255, 255, 0.05)' }}>
                <MessageSquare size={32} color="#71717a" style={{ marginBottom: '16px' }} />
                <p style={{ color: '#a1a1aa', margin: 0 }}>No recent chat sessions in the last 3 days.</p>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );

  const StatCard = ({ title, value, icon, color }) => (
    <div style={{
      background: 'rgba(24, 24, 27, 0.6)',
      border: '1px solid rgba(255, 255, 255, 0.05)',
      borderRadius: '20px',
      padding: '24px',
      display: 'flex',
      flexDirection: 'column',
      gap: '12px',
      transition: 'transform 0.2s',
      cursor: 'default'
    }}
      onMouseOver={e => e.currentTarget.style.transform = 'translateY(-2px)'}
      onMouseOut={e => e.currentTarget.style.transform = 'translateY(0)'}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <p style={{ margin: 0, fontSize: '14px', color: '#a1a1aa' }}>{title}</p>
        <div style={{ padding: '8px', borderRadius: '10px', background: color }}>
          {icon}
        </div>
      </div>
      <p style={{ margin: 0, fontSize: '28px', fontWeight: '700', color: '#fafafa' }}>{value}</p>
    </div>
  );

  const Badge = ({ type }) => {
    let color = '#a1a1aa';
    let bg = 'rgba(63, 63, 70, 0.3)';
    if (type === 'HIGH') { color = '#4ade80'; bg = 'rgba(74, 222, 128, 0.1)'; }
    if (type === 'MEDIUM') { color = '#fbbf24'; bg = 'rgba(251, 191, 36, 0.1)'; }
    if (type === 'LOW') { color = '#f87171'; bg = 'rgba(248, 113, 113, 0.1)'; }

    return (
      <span style={{
        fontSize: '11px',
        fontWeight: '600',
        padding: '2px 8px',
        borderRadius: '12px',
        color,
        background: bg,
        border: `1px solid ${color}30`
      }}>
        {type}
      </span>
    );
  };

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#09090b',
      color: '#fafafa',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      position: 'relative',
      overflow: 'hidden',
    }}>
      <style>{`
        * {
          -webkit-overflow-scrolling: touch;
          box-sizing: border-box;
        }
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @media (max-width: 768px) {
          .wrapper { padding: 16px !important; }
          .header { padding: 12px 16px !important; }
          .tab-container { 
            -webkit-overflow-scrolling: touch;
            scrollbar-width: none;
          }
          .tab-container::-webkit-scrollbar {
            display: none;
          }
          .button-group { 
            flex-direction: column !important;
            width: 100% !important;
          }
          .button-group button {
            width: 100% !important;
          }
        }
      `}</style>

      {/* Gradient Background */}
      <div style={{
        position: 'fixed',
        top: '-50%',
        left: '-50%',
        width: '200%',
        height: '200%',
        background: 'radial-gradient(circle at 50% 50%, rgba(139, 92, 246, 0.1) 0%, transparent 50%)',
        pointerEvents: 'none',
        zIndex: 0,
      }} />

      {/* Header */}
      <div className="header" style={{
        padding: '20px 32px',
        borderBottom: '1px solid rgba(39, 39, 42, 0.5)',
        backdropFilter: 'blur(10px)',
        backgroundColor: 'rgba(9, 9, 11, 0.8)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        position: 'sticky',
        top: 0,
        zIndex: 10,
        gap: '16px',
        flexWrap: 'wrap',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <button
            onClick={handleBackToChatClick}
            style={{
              backgroundColor: 'rgba(39, 39, 42, 0.5)',
              color: '#a1a1aa',
              border: '1px solid rgba(63, 63, 70, 0.5)',
              padding: '8px 12px',
              borderRadius: '8px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              transition: 'all 0.2s',
              backdropFilter: 'blur(10px)',
              fontSize: '14px',
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(63, 63, 70, 0.5)';
              e.currentTarget.style.color = '#fafafa';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(39, 39, 42, 0.5)';
              e.currentTarget.style.color = '#a1a1aa';
            }}
          >
            <ArrowLeft size={16} />
            <span style={{ display: window.innerWidth < 400 ? 'none' : 'inline' }}>Back</span>
          </button>
          <div>
            <h1 style={{
              fontSize: 'clamp(18px, 4vw, 24px)',
              fontWeight: '600',
              marginBottom: '4px',
              letterSpacing: '-0.02em',
            }}>
              Admin Console
            </h1>
            <p style={{ fontSize: 'clamp(12px, 2vw, 14px)', color: '#71717a' }}>
              Knowledge management & testing
            </p>
          </div>
        </div>
      </div>

      <div className="wrapper" style={{ maxWidth: '1200px', margin: '0 auto', padding: '32px 16px', position: 'relative', zIndex: 1 }}>
        {/* Tabs */}
        <div className="tab-container" style={{
          display: 'flex',
          gap: '4px',
          marginBottom: '32px',
          backgroundColor: 'rgba(24, 24, 27, 0.5)',
          padding: '4px',
          borderRadius: '12px',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(39, 39, 42, 0.5)',
          width: '100%',
          overflowX: 'auto',
        }}>
          {tabs.map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab.id)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '10px 16px',
                  borderRadius: '8px',
                  border: 'none',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  position: 'relative',
                  whiteSpace: 'nowrap',
                  fontSize: '14px',
                  minWidth: 'fit-content',
                  backgroundColor: activeTab === tab.id ? 'rgba(139, 92, 246, 0.15)' : 'transparent',
                  color: activeTab === tab.id ? '#a78bfa' : '#71717a',
                }}
                onMouseOver={(e) => {
                  if (activeTab !== tab.id) {
                    e.currentTarget.style.backgroundColor = 'rgba(63, 63, 70, 0.3)';
                    e.currentTarget.style.color = '#a1a1aa';
                  }
                }}
                onMouseOut={(e) => {
                  if (activeTab !== tab.id) {
                    e.currentTarget.style.backgroundColor = 'transparent';
                    e.currentTarget.style.color = '#71717a';
                  }
                }}
              >
                <Icon size={18} />
                <span>{tab.label}</span>
                {tab.badge > 0 && (
                  <span style={{
                    backgroundColor: '#ef4444',
                    color: 'white',
                    fontSize: '11px',
                    padding: '2px 6px',
                    borderRadius: '10px',
                    fontWeight: '600',
                    marginLeft: '4px',
                  }}>
                    {tab.badge}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Upload Tab */}
        {activeTab === 'dashboard' && renderDashboard()}
        {activeTab === 'upload' && (
          <form onSubmit={handleUpload} style={{ display: 'flex', flexDirection: 'column', gap: '24px', animation: 'slideUp 0.4s ease-out' }}>
            {/* Compact Upload Card */}
            <div style={{
              backgroundColor: 'rgba(24, 24, 27, 0.5)',
              border: '1px solid rgba(63, 63, 70, 0.5)',
              borderRadius: '16px',
              padding: '20px',
              backdropFilter: 'blur(10px)',
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
            }}>

              {/* Row 1: Filename & Tags */}
              <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                <div style={{ flex: 1, minWidth: '200px' }}>
                  <label style={{ display: 'block', fontSize: '13px', color: '#a1a1aa', marginBottom: '6px', fontWeight: '500' }}>
                    Filename
                  </label>
                  <input
                    type="text"
                    value={uploadFilename}
                    onChange={(e) => setUploadFilename(e.target.value)}
                    placeholder="e.g., feature-specs.txt"
                    style={{
                      width: '100%',
                      backgroundColor: 'rgba(9, 9, 11, 0.8)',
                      border: '1px solid rgba(63, 63, 70, 0.5)',
                      borderRadius: '8px',
                      padding: '8px 12px',
                      color: '#fafafa',
                      fontSize: '13px',
                    }}
                  />
                </div>
                <div style={{ flex: 1, minWidth: '200px' }}>
                  <label style={{ display: 'block', fontSize: '13px', color: '#a1a1aa', marginBottom: '6px', fontWeight: '500' }}>
                    Tags <span style={{ fontSize: '11px', fontWeight: '400', opacity: 0.7 }}>(comma separated)</span>
                  </label>
                  <input
                    type="text"
                    value={uploadTags}
                    onChange={(e) => setUploadTags(e.target.value)}
                    placeholder="policy, billing, etc."
                    style={{
                      width: '100%',
                      backgroundColor: 'rgba(9, 9, 11, 0.8)',
                      border: '1px solid rgba(63, 63, 70, 0.5)',
                      borderRadius: '8px',
                      padding: '8px 12px',
                      color: '#fafafa',
                      fontSize: '13px',
                    }}
                  />
                </div>
              </div>

              {/* Row 2: Source URL */}
              <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
                <div style={{ flex: 1, minWidth: '200px' }}>
                  <label style={{ display: 'block', fontSize: '13px', color: '#a1a1aa', marginBottom: '6px', fontWeight: '500' }}>
                    Source URL <span style={{ fontSize: '11px', fontWeight: '400', opacity: 0.7 }}>(optional)</span>
                  </label>
                  <input
                    type="url"
                    value={sourceUrl}
                    onChange={(e) => setSourceUrl(e.target.value)}
                    placeholder="https://..."
                    style={{
                      width: '100%',
                      backgroundColor: 'rgba(9, 9, 11, 0.8)',
                      border: '1px solid rgba(63, 63, 70, 0.5)',
                      borderRadius: '8px',
                      padding: '8px 12px',
                      color: '#fafafa',
                      fontSize: '13px',
                    }}
                  />
                </div>
              </div>

              {/* Row 3: Content Area */}
              <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
                <label style={{ display: 'block', fontSize: '13px', color: '#a1a1aa', marginBottom: '6px', fontWeight: '500' }}>
                  Raw Knowledge Content
                </label>
                <textarea
                  value={uploadContent}
                  onChange={(e) => setUploadContent(e.target.value)}
                  placeholder={'Paste text here...'}
                  style={{
                    width: '100%',
                    backgroundColor: 'rgba(9, 9, 11, 0.8)',
                    border: '1px solid rgba(63, 63, 70, 0.5)',
                    borderRadius: '8px',
                    padding: '12px',
                    color: '#fafafa',
                    fontSize: '13px',
                    fontFamily: 'monospace',
                    resize: 'vertical',
                    lineHeight: '1.5',
                    minHeight: '180px',
                    height: '240px',
                  }}
                />
              </div>
            </div>

            <div style={{ display: 'flex', gap: '12px', marginTop: 'auto' }}>
              <button
                type="submit"
                disabled={loading}
                style={{
                  flex: 1,
                  background: loading ? 'rgba(63, 63, 70, 0.5)' : 'linear-gradient(135deg, #10b981, #059669)',
                  color: loading ? '#71717a' : 'white',
                  padding: '14px',
                  borderRadius: '12px',
                  border: 'none',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  fontSize: '15px',
                  fontWeight: '600',
                  transition: 'all 0.2s',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                }}
              >
                {loading ? 'Uploading...' : 'Upload Document'}
              </button>
            </div>

            {uploadStatus && (
              <p style={{
                marginTop: '16px',
                padding: '12px',
                borderRadius: '8px',
                backgroundColor: uploadStatus.includes('Error') ? 'rgba(239, 68, 68, 0.1)' : 'rgba(16, 185, 129, 0.1)',
                color: uploadStatus.includes('Error') ? '#ef4444' : '#10b981',
                fontSize: '14px',
                textAlign: 'center'
              }}>
                {uploadStatus}
              </p>
            )}
          </form>
        )}

        {/* Knowledge Base Tab */}
        {activeTab === 'knowledge' && (
          <div style={{ animation: 'slideUp 0.4s ease-out' }}>

            <div style={{ display: 'flex', gap: '16px', marginBottom: '24px' }}>
              <div style={{ position: 'relative', flex: 1 }}>
                <ScanSearch size={20} color="#71717a" style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)' }} />
                <input
                  type="text"
                  placeholder="Search documents..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  style={{
                    width: '100%',
                    backgroundColor: 'rgba(24, 24, 27, 0.5)',
                    border: '1px solid rgba(63, 63, 70, 0.5)',
                    borderRadius: '12px',
                    padding: '12px 16px 12px 44px',
                    color: '#fafafa',
                    fontSize: '14px',
                  }}
                />
              </div>

              <div style={{ position: 'relative', minWidth: '200px' }}>
                <select
                  value={filterTag}
                  onChange={(e) => setFilterTag(e.target.value)}
                  style={{
                    width: '100%',
                    backgroundColor: 'rgba(24, 24, 27, 0.5)',
                    border: '1px solid rgba(63, 63, 70, 0.5)',
                    borderRadius: '12px',
                    padding: '12px 16px',
                    color: '#fafafa',
                    fontSize: '14px',
                    appearance: 'none',
                    cursor: 'pointer',
                  }}
                >
                  <option value="">All Tags</option>
                  {availableTags.map(tag => (
                    <option key={tag} value={tag}>{tag}</option>
                  ))}
                </select>
                <div style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', color: '#71717a', fontSize: '10px' }}>▼</div>
              </div>
            </div>

            <p style={{ color: '#71717a', marginBottom: '24px', fontSize: '14px' }}>
              {documents.filter(doc =>
                searchQuery === '' ||
                doc.filename.toLowerCase().includes(searchQuery.toLowerCase())
              ).length} documents
            </p>

            {showTagEdit && (
              <div style={{
                marginBottom: '24px',
                padding: '16px',
                backgroundColor: 'rgba(139, 92, 246, 0.1)',
                border: '1px solid rgba(139, 92, 246, 0.2)',
                borderRadius: '12px',
                display: 'flex',
                gap: '12px',
                alignItems: 'flex-end',
                animation: 'slideUp 0.2s ease-out'
              }}>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', fontSize: '12px', color: '#a78bfa', marginBottom: '8px' }}>
                    Editing tags for: <b>{tagEditFilename}</b>
                  </label>
                  <input
                    type="text"
                    value={editingTags}
                    onChange={(e) => setEditingTags(e.target.value)}
                    placeholder="tag1, tag2, tag3"
                    style={{
                      width: '100%',
                      backgroundColor: 'rgba(9, 9, 11, 0.8)',
                      border: '1px solid rgba(139, 92, 246, 0.3)',
                      borderRadius: '8px',
                      padding: '8px 12px',
                      color: '#fafafa',
                      fontSize: '14px',
                    }}
                  />
                </div>
                <button
                  onClick={handleUpdateTags}
                  disabled={loading}
                  style={{
                    padding: '9px 16px',
                    backgroundColor: '#8b5cf6',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '13px',
                    fontWeight: '500',
                    cursor: 'pointer',
                  }}
                >
                  Save
                </button>
                <button
                  onClick={() => setShowTagEdit(false)}
                  style={{
                    padding: '9px 16px',
                    backgroundColor: 'transparent',
                    color: '#a1a1aa',
                    border: '1px solid rgba(63, 63, 70, 0.5)',
                    borderRadius: '8px',
                    fontSize: '13px',
                    fontWeight: '500',
                    cursor: 'pointer',
                  }}
                >
                  Cancel
                </button>
              </div>
            )}

            {isEditing ? (
              <div style={{
                backgroundColor: 'rgba(24, 24, 27, 0.5)',
                border: '1px solid rgba(139, 92, 246, 0.3)',
                borderRadius: '16px',
                padding: '24px',
                backdropFilter: 'blur(10px)',
                animation: 'slideUp 0.3s ease-out',
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px', alignItems: 'center' }}>
                  <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600', color: '#fff' }}>
                    Editing: <span style={{ color: '#a78bfa' }}>{editingDoc.originalFilename}</span>
                  </h3>
                  <button
                    onClick={() => { setIsEditing(false); setEditingDoc(null); }}
                    style={{ background: 'none', border: 'none', color: '#71717a', cursor: 'pointer' }}
                  >
                    ✕
                  </button>
                </div>

                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', fontSize: '14px', color: '#a1a1aa', marginBottom: '8px' }}>Content</label>
                  <textarea
                    value={editingDoc.content}
                    onChange={(e) => setEditingDoc(prev => ({ ...prev, content: e.target.value }))}
                    style={{
                      width: '100%',
                      backgroundColor: 'rgba(9, 9, 11, 0.8)',
                      border: '1px solid rgba(63, 63, 70, 0.5)',
                      borderRadius: '12px',
                      padding: '16px',
                      color: '#fafafa',
                      fontSize: '14px',
                      fontFamily: 'monospace',
                      minHeight: '300px',
                      resize: 'vertical',
                    }}
                  />
                </div>

                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', fontSize: '14px', color: '#a1a1aa', marginBottom: '8px' }}>Source URL</label>
                  <input
                    type="url"
                    value={editingDoc.sourceUrl}
                    onChange={(e) => setEditingDoc(prev => ({ ...prev, sourceUrl: e.target.value }))}
                    style={{
                      width: '100%',
                      backgroundColor: 'rgba(9, 9, 11, 0.8)',
                      border: '1px solid rgba(63, 63, 70, 0.5)',
                      borderRadius: '12px',
                      padding: '12px 16px',
                      color: '#fafafa',
                      fontSize: '14px',
                    }}
                  />
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
                  <input
                    type="checkbox"
                    id="skipConversion"
                    checked={editingDoc.skipConversion}
                    onChange={(e) => setEditingDoc(prev => ({ ...prev, skipConversion: e.target.checked }))}
                    style={{ width: '16px', height: '16px', accentColor: '#8b5cf6' }}
                  />
                  <label htmlFor="skipConversion" style={{ color: '#e4e4e7', fontSize: '14px' }}>
                    Skip canonical conversion (recommended for minor edits)
                  </label>
                </div>

                <div style={{ display: 'flex', gap: '12px' }}>
                  <button
                    onClick={handleSaveEdit}
                    disabled={loading}
                    style={{
                      padding: '12px 24px',
                      backgroundColor: loading ? 'rgba(63, 63, 70, 0.5)' : '#8b5cf6',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      fontWeight: '500',
                      cursor: loading ? 'not-allowed' : 'pointer',
                      flex: 1,
                    }}
                  >
                    {loading ? 'Saving...' : 'Save Changes'}
                  </button>
                  <button
                    onClick={() => { setIsEditing(false); setEditingDoc(null); }}
                    disabled={loading}
                    style={{
                      padding: '12px 24px',
                      backgroundColor: 'rgba(39, 39, 42, 0.5)',
                      color: '#a1a1aa',
                      border: '1px solid rgba(63, 63, 70, 0.5)',
                      borderRadius: '8px',
                      fontWeight: '500',
                      cursor: 'pointer',
                      flex: 1,
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {groupedDocuments
                  .filter(doc => {
                    const matchesSearch = searchQuery === '' || doc.filename.toLowerCase().includes(searchQuery.toLowerCase());
                    const matchesTag = filterTag === '' || (doc.tags && doc.tags.includes(filterTag));
                    return matchesSearch && matchesTag;
                  })
                  .map(group => (
                    <div
                      key={group.filename}
                      style={{
                        backgroundColor: 'rgba(24, 24, 27, 0.5)',
                        border: '1px solid rgba(63, 63, 70, 0.5)',
                        borderRadius: '12px',
                        overflow: 'hidden',
                        transition: 'all 0.2s',
                      }}
                    >
                      <div
                        onClick={() => toggleGroup(group.filename)}
                        style={{
                          padding: '16px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          cursor: 'pointer',
                          backgroundColor: expandedGroups.has(group.filename) ? 'rgba(63, 63, 70, 0.2)' : 'transparent',
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <Database size={20} color="#a1a1aa" />
                          <div>
                            <p style={{ margin: '0 0 4px 0', fontSize: '15px', fontWeight: '500', color: '#fafafa' }}>
                              {group.filename}
                            </p>
                            <div style={{ display: 'flex', gap: '12px', fontSize: '13px', color: '#a1a1aa' }}>
                              <span>{group.count} chunk{group.count !== 1 ? 's' : ''}</span>
                              <span>•</span>
                              <span>{new Date(group.lastModified).toLocaleDateString()}</span>
                              {group.sourceUrl && (
                                <>
                                  <span>•</span>
                                  <span style={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#8b5cf6' }}>
                                    Link
                                  </span>
                                </>
                              )}



                              {/* Tags */}
                              {group.tags && group.tags.length > 0 && (
                                <>
                                  <span style={{ margin: '0 4px' }}>|</span>
                                  <div style={{ display: 'flex', gap: '6px' }}>
                                    {group.tags.map(tag => (
                                      <span key={tag} style={{
                                        fontSize: '11px',
                                        backgroundColor: 'rgba(39, 39, 42, 0.8)',
                                        color: '#a1a1aa',
                                        padding: '2px 6px',
                                        borderRadius: '4px',
                                        border: '1px solid rgba(255, 255, 255, 0.05)'
                                      }}>
                                        #{tag}
                                      </span>
                                    ))}
                                  </div>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setTagEditFilename(group.filename);
                              setEditingTags(group.tags ? group.tags.join(', ') : '');
                              setShowTagEdit(true);
                            }}
                            style={{
                              padding: '6px',
                              backgroundColor: 'transparent',
                              border: 'none',
                              color: '#71717a',
                              cursor: 'pointer',
                              opacity: 0.6,
                            }}
                            title="Edit Tags"
                          >
                            <div style={{ fontSize: '10px' }}>🏷️</div>
                          </button>

                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEdit(group.filename, group.sourceUrl);
                            }}
                            style={{
                              padding: '6px 12px',
                              backgroundColor: 'rgba(139, 92, 246, 0.1)',
                              border: '1px solid rgba(139, 92, 246, 0.3)',
                              borderRadius: '6px',
                              color: '#a78bfa',
                              fontSize: '13px',
                              fontWeight: '500',
                              cursor: 'pointer',
                            }}
                          >
                            Edit
                          </button>
                        </div>
                      </div>

                      {expandedGroups.has(group.filename) && (
                        <div style={{ padding: '0 16px 16px 16px', borderTop: '1px solid rgba(63, 63, 70, 0.3)' }}>
                          <p style={{ fontSize: '13px', color: '#71717a', marginTop: '12px', marginBottom: '8px' }}>
                            Contains {group.count} chunks. Click "Edit" to modify content or source URL.
                          </p>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            {documents
                              .filter(d => d.filename.startsWith(group.filename))
                              .slice(0, 5)
                              .map(chunk => (
                                <div key={chunk.id} style={{
                                  fontSize: '12px',
                                  color: '#a1a1aa',
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  padding: '4px 0'
                                }}>
                                  <span>{chunk.filename}</span>
                                  <button
                                    onClick={() => handleDelete(chunk.id)}
                                    style={{ color: '#ef4444', background: 'none', border: 'none', cursor: 'pointer' }}
                                  >
                                    Delete Chunk
                                  </button>
                                </div>
                              ))
                            }
                            {group.count > 5 && (
                              <div style={{ fontSize: '12px', color: '#71717a', fontStyle: 'italic' }}>
                                ...and {group.count - 5} more chunks
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}

                {groupedDocuments.filter(doc =>
                  searchQuery === '' ||
                  doc.filename.toLowerCase().includes(searchQuery.toLowerCase())
                ).length === 0 && (
                    <div style={{
                      textAlign: 'center',
                      padding: '80px 16px',
                      color: '#71717a',
                      backgroundColor: 'rgba(24, 24, 27, 0.3)',
                      borderRadius: '16px',
                      border: '1px dashed rgba(63, 63, 70, 0.5)',
                    }}>
                      <Database size={48} style={{ margin: '0 auto 16px', opacity: 0.3 }} />
                      <p>No documents found</p>
                    </div>
                  )}
              </div>
            )}
          </div>
        )}

        {/* Unanswered Questions & Flagged Responses Tab (Unified Actionable Flow) */}
        {activeTab === 'unanswered' && (() => {
          const pendingQuestions = unansweredQuestions.filter(q => q.status === 'pending');
          const flaggedCount = pendingQuestions.filter(q => {
            const gap = parseGapAnalysis(q);
            return gap?.entry_type === 'flagged_response';
          }).length;
          const unansweredCount = pendingQuestions.length - flaggedCount;

          const filteredQuestions = pendingQuestions.filter(q => {
            const gap = parseGapAnalysis(q);
            const isFlagged = gap?.entry_type === 'flagged_response';
            if (unansweredFilter === 'flagged') return isFlagged;
            if (unansweredFilter === 'unanswered') return !isFlagged;
            return true;
          });

          return (
          <div style={{ animation: 'slideUp 0.4s ease-out' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '12px' }}>
              {/* Filter Pills */}
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <button
                  onClick={() => setUnansweredFilter('all')}
                  style={{
                    padding: '6px 14px',
                    borderRadius: '20px',
                    fontSize: '13px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    border: unansweredFilter === 'all' ? '1px solid #8b5cf6' : '1px solid rgba(63, 63, 70, 0.4)',
                    backgroundColor: unansweredFilter === 'all' ? 'rgba(139, 92, 246, 0.2)' : 'rgba(24, 24, 27, 0.5)',
                    color: unansweredFilter === 'all' ? '#c4b5fd' : '#a1a1aa',
                  }}
                >
                  All Items ({pendingQuestions.length})
                </button>
                <button
                  onClick={() => setUnansweredFilter('unanswered')}
                  style={{
                    padding: '6px 14px',
                    borderRadius: '20px',
                    fontSize: '13px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    border: unansweredFilter === 'unanswered' ? '1px solid #f59e0b' : '1px solid rgba(63, 63, 70, 0.4)',
                    backgroundColor: unansweredFilter === 'unanswered' ? 'rgba(245, 158, 11, 0.2)' : 'rgba(24, 24, 27, 0.5)',
                    color: unansweredFilter === 'unanswered' ? '#fbbf24' : '#a1a1aa',
                  }}
                >
                  ⚠️ Unanswered Escalations ({unansweredCount})
                </button>
                <button
                  onClick={() => setUnansweredFilter('flagged')}
                  style={{
                    padding: '6px 14px',
                    borderRadius: '20px',
                    fontSize: '13px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    border: unansweredFilter === 'flagged' ? '1px solid #ef4444' : '1px solid rgba(63, 63, 70, 0.4)',
                    backgroundColor: unansweredFilter === 'flagged' ? 'rgba(239, 68, 68, 0.2)' : 'rgba(24, 24, 27, 0.5)',
                    color: unansweredFilter === 'flagged' ? '#fca5a5' : '#a1a1aa',
                  }}
                >
                  🚩 Flagged Responses ({flaggedCount})
                </button>
              </div>

              <button
                onClick={fetchUnanswered}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  backgroundColor: 'rgba(39, 39, 42, 0.5)',
                  color: '#a1a1aa',
                  border: '1px solid rgba(63, 63, 70, 0.4)',
                  borderRadius: '8px',
                  padding: '6px 12px',
                  fontSize: '12px',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                }}
              >
                <RefreshCw size={13} className={loading ? 'animate-spin' : ''} />
                Refresh
              </button>
            </div>

            {/* Questions List */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {filteredQuestions
                .map(q => {
                  const gap = parseGapAnalysis(q);
                  const isFlagged = gap?.entry_type === 'flagged_response';
                  const gapCfg = gap?.gap_type && GAP_TYPE_CONFIG[gap.gap_type] ? GAP_TYPE_CONFIG[gap.gap_type] : (gap?.gap_type ? {
                    label: gap.gap_type,
                    color: isFlagged ? '#ef4444' : '#71717a',
                    bg: isFlagged ? 'rgba(239, 68, 68, 0.12)' : 'rgba(113, 113, 122, 0.12)',
                    border: isFlagged ? 'rgba(239, 68, 68, 0.3)' : 'rgba(113, 113, 122, 0.3)',
                    icon: isFlagged ? Flag : AlertCircle,
                    description: ''
                  } : null);

                  const GapIcon = isFlagged ? Flag : (gapCfg?.icon || AlertCircle);
                  const isExpanded = expandedItems.has(q.id) || expandedItems.has(String(q.id)) || expandedItems.has(Number(q.id));

                  return (
                    <div
                      key={q.id}
                      id={`unanswered-card-${q.id}`}
                      style={{
                        backgroundColor: 'rgba(24, 24, 27, 0.75)',
                        border: '1px solid rgba(255, 255, 255, 0.08)',
                        borderLeft: isFlagged 
                          ? '4px solid #ef4444' 
                          : (gapCfg ? `4px solid ${gapCfg.color}` : '4px solid rgba(239, 68, 68, 0.6)'),
                        borderRadius: '16px',
                        padding: '22px 24px',
                        backdropFilter: 'blur(12px)',
                        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.25)',
                        transition: 'all 0.2s ease',
                      }}
                    >
                      {/* Card Header (Click to expand/collapse) */}
                      <div
                        onClick={() => toggleExpanded(q.id)}
                        style={{ cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: '10px' }}
                      >
                        {/* Top Badges & Meta */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                            {isFlagged ? (
                              <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                                backgroundColor: 'rgba(239, 68, 68, 0.15)',
                                border: '1px solid rgba(239, 68, 68, 0.4)',
                                borderRadius: '8px',
                                padding: '4px 10px',
                              }}>
                                <Flag size={13} style={{ color: '#ef4444' }} />
                                <span style={{ fontSize: '11px', fontWeight: '700', color: '#fca5a5', letterSpacing: '0.02em', textTransform: 'uppercase' }}>
                                  🚩 Flagged Response ({gapCfg?.label || gap?.diagnosis_title || gap?.gap_type || 'Reported Issue'})
                                </span>
                              </div>
                            ) : gapCfg ? (
                              <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                                backgroundColor: gapCfg.bg,
                                border: `1px solid ${gapCfg.border}`,
                                borderRadius: '8px',
                                padding: '4px 10px',
                              }}>
                                <GapIcon size={13} style={{ color: gapCfg.color }} />
                                <span style={{ fontSize: '11px', fontWeight: '600', color: gapCfg.color, letterSpacing: '0.02em', textTransform: 'uppercase' }}>
                                  {gapCfg.label}
                                </span>
                              </div>
                            ) : (
                              <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                                backgroundColor: 'rgba(239, 68, 68, 0.1)',
                                border: '1px solid rgba(239, 68, 68, 0.3)',
                                borderRadius: '8px',
                                padding: '4px 10px',
                              }}>
                                <AlertCircle size={13} style={{ color: '#ef4444' }} />
                                <span style={{ fontSize: '11px', fontWeight: '600', color: '#ef4444' }}>
                                  Unanswered
                                </span>
                              </div>
                            )}

                            {/* Assigned Expert Badge */}
                            {gap?.assigned_expert && (
                              <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '5px',
                                backgroundColor: 'rgba(56, 189, 248, 0.1)',
                                border: '1px solid rgba(56, 189, 248, 0.3)',
                                borderRadius: '8px',
                                padding: '4px 10px',
                              }}>
                                <span style={{ fontSize: '11px', fontWeight: '600', color: '#38bdf8' }}>
                                  👤 {gap.assigned_expert.expertName}
                                </span>
                              </div>
                            )}

                            {/* Zoho Ticket Badge */}
                            {gap?.zoho_ticket?.ticket_number && (
                              <a
                                href={gap.zoho_ticket.ticket_id ? `https://desk.zoho.in/support/zuppler/ShowHomePage.do#Cases/dv/${gap.zoho_ticket.ticket_id}` : 'https://desk.zoho.in'}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '4px',
                                  backgroundColor: 'rgba(245, 158, 11, 0.1)',
                                  border: '1px solid rgba(245, 158, 11, 0.3)',
                                  borderRadius: '8px',
                                  padding: '4px 8px',
                                  textDecoration: 'none',
                                  fontSize: '11px',
                                  fontWeight: '600',
                                  color: '#f59e0b',
                                }}
                              >
                                🎫 Zoho #{gap.zoho_ticket.ticket_number}
                              </a>
                            )}
                          </div>

                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            {q.user_name && (
                              <span style={{ fontSize: '12px', color: '#fbbf24', display: 'flex', alignItems: 'center', gap: '4px', fontWeight: '500' }}>
                                👤 {q.user_name}
                              </span>
                            )}
                            <span style={{ fontSize: '12px', color: '#71717a' }}>
                              {new Date(q.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                            </span>
                            <div style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              width: '24px',
                              height: '24px',
                              borderRadius: '6px',
                              backgroundColor: 'rgba(255, 255, 255, 0.05)',
                              color: '#a1a1aa'
                            }}>
                              {isExpanded ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
                            </div>
                          </div>
                        </div>

                        {/* Question Title & Resolved Context */}
                        <div>
                          <p style={{
                            margin: 0,
                            fontWeight: '600',
                            fontSize: '15px',
                            color: '#fafafa',
                            lineHeight: '1.4',
                            letterSpacing: '-0.01em'
                          }}>
                            {gap?.resolved_question || q.question}
                          </p>

                          {gap?.original_question && gap.original_question !== (gap?.resolved_question || q.question) && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '6px' }}>
                              <CornerDownRight size={13} style={{ color: '#71717a' }} />
                              <span style={{ fontSize: '12px', color: '#a1a1aa' }}>
                                Original follow-up: <em style={{ color: '#d4d4d8' }}>"{gap.original_question}"</em>
                              </span>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Expanded Details */}
                      {isExpanded && (() => {
                        const initialSuggestedDoc = gap?.suggested_target_document?.title || null;
                        const chosenTargetDoc = selectedTargetDocs[q.id] !== undefined ? selectedTargetDocs[q.id] : initialSuggestedDoc;

                        return (
                        <div style={{ marginTop: '20px', display: 'flex', flexDirection: 'column', gap: '16px', animation: 'slideUp 0.2s ease-out' }}>
                          
                          {/* Flagged Specific: Reported Feedback Box */}
                          {isFlagged && (
                            <div style={{
                              backgroundColor: 'rgba(239, 68, 68, 0.08)',
                              border: '1px solid rgba(239, 68, 68, 0.25)',
                              borderRadius: '12px',
                              padding: '14px 18px',
                            }}>
                              <span style={{ fontSize: '11px', fontWeight: '700', color: '#fca5a5', textTransform: 'uppercase', letterSpacing: '0.04em', display: 'block', marginBottom: '6px' }}>
                                💬 Teammate's Reported Reason / Feedback
                              </span>
                              <p style={{ margin: 0, fontSize: '14px', color: '#fafafa', fontWeight: '500', lineHeight: '1.5' }}>
                                "{gap?.user_feedback || 'No written reason provided'}"
                              </p>
                            </div>
                          )}

                          {/* View Full Chat Transcript Modal Button */}
                          {(gap?.session_id || gap?.chat_log_id) && (
                            <div style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              flexWrap: 'wrap',
                              gap: '12px',
                              backgroundColor: 'rgba(24, 24, 27, 0.6)',
                              border: '1px solid rgba(63, 63, 70, 0.5)',
                              borderRadius: '12px',
                              padding: '12px 18px',
                            }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <div style={{ padding: '8px', borderRadius: '8px', backgroundColor: 'rgba(59, 130, 246, 0.15)' }}>
                                  <MessageSquare size={16} color="#60a5fa" />
                                </div>
                                <div>
                                  <span style={{ fontSize: '13px', fontWeight: '600', color: '#fafafa', display: 'block' }}>
                                    Full Conversation Session
                                  </span>
                                  <span style={{ fontSize: '12px', color: '#a1a1aa' }}>
                                    Inspect entire multi-turn user exchange & context
                                  </span>
                                </div>
                              </div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <button
                                  onClick={() => openSessionModal(gap?.session_id || gap?.chat_log_id)}
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    padding: '7px 14px',
                                    borderRadius: '8px',
                                    backgroundColor: 'rgba(59, 130, 246, 0.2)',
                                    border: '1px solid rgba(59, 130, 246, 0.4)',
                                    color: '#60a5fa',
                                    fontSize: '12px',
                                    fontWeight: '600',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s',
                                  }}
                                >
                                  <Eye size={13} /> View Chat Transcript
                                </button>
                              </div>
                            </div>
                          )}

                          {/* Diagnosis & Root Cause */}
                          <div style={{
                            backgroundColor: gapCfg ? gapCfg.bg : 'rgba(255, 255, 255, 0.03)',
                            border: gapCfg ? `1px solid ${gapCfg.border}` : '1px solid rgba(255, 255, 255, 0.08)',
                            borderRadius: '14px',
                            padding: '18px 20px',
                          }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                              <GapIcon size={16} style={{ color: gapCfg ? gapCfg.color : '#e4e4e7' }} />
                              <span style={{ fontSize: '14px', fontWeight: '600', color: gapCfg ? gapCfg.color : '#fafafa' }}>
                                {isFlagged ? `AI Root Cause Diagnosis: ${gapCfg?.label || gap?.gap_type}` : `Diagnosis: ${gapCfg ? gapCfg.label : 'Knowledge Gap'}`}
                              </span>
                            </div>

                            <p style={{ margin: '0 0 10px 0', fontSize: '13px', color: '#f4f4f5', lineHeight: '1.5' }}>
                              {gap?.gap_explanation || gap?.explanation || (!q.context?.startsWith('{') ? q.context : 'Sage was unable to find documentation that answers this question.')}
                            </p>

                            {gap?.missing_information && (
                              <div style={{ borderTop: '1px solid rgba(255, 255, 255, 0.08)', paddingTop: '10px', marginTop: '10px' }}>
                                <span style={{ fontSize: '11px', fontWeight: '600', color: '#a1a1aa', textTransform: 'uppercase', letterSpacing: '0.04em', display: 'block', marginBottom: '4px' }}>
                                  Missing Information
                                </span>
                                <p style={{ margin: 0, fontSize: '13px', color: '#d4d4d8', lineHeight: '1.5' }}>
                                  {gap.missing_information}
                                </p>
                              </div>
                            )}
                          </div>

                          {/* Target Document & Recommended Solution */}
                          <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                            gap: '14px',
                          }}>
                            {/* Target Document Card with Searchable Document Override */}
                            <div style={{
                              backgroundColor: 'rgba(255, 255, 255, 0.03)',
                              border: '1px solid rgba(255, 255, 255, 0.06)',
                              borderRadius: '12px',
                              padding: '16px',
                            }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                                <span style={{ fontSize: '11px', fontWeight: '600', color: '#71717a', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                  Target Document
                                </span>
                                <button
                                  onClick={() => setShowDocPicker(prev => ({ ...prev, [q.id]: !prev[q.id] }))}
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '4px',
                                    background: 'transparent',
                                    border: '1px solid rgba(63, 63, 70, 0.4)',
                                    borderRadius: '6px',
                                    color: '#38bdf8',
                                    padding: '3px 8px',
                                    fontSize: '11px',
                                    fontWeight: '500',
                                    cursor: 'pointer'
                                  }}
                                >
                                  <Search size={11} /> {showDocPicker[q.id] ? 'Close Picker' : 'Choose Different Doc'}
                                </button>
                              </div>

                              {chosenTargetDoc ? (
                                <div>
                                  <p style={{ margin: '0 0 4px 0', fontSize: '14px', fontWeight: '600', color: '#38bdf8' }}>
                                    📄 {chosenTargetDoc}
                                    {chosenTargetDoc === gap?.suggested_target_document?.title && gap?.suggested_target_document?.section && (
                                      <span style={{ color: '#a1a1aa', fontWeight: '400', fontSize: '13px' }}>
                                        {' '}→ Section: <strong style={{ color: '#f4f4f5' }}>{gap.suggested_target_document.section}</strong>
                                      </span>
                                    )}
                                    {chosenTargetDoc !== gap?.suggested_target_document?.title && (
                                      <span style={{ color: '#22c55e', fontSize: '11px', marginLeft: '6px', fontWeight: '600', backgroundColor: 'rgba(34, 197, 94, 0.15)', padding: '2px 6px', borderRadius: '4px' }}>
                                        Custom Selected
                                      </span>
                                    )}
                                  </p>
                                  <p style={{ margin: 0, fontSize: '12px', color: '#a1a1aa', lineHeight: '1.4' }}>
                                    {chosenTargetDoc === gap?.suggested_target_document?.title ? (gap?.suggested_target_document?.reason || 'Primary matching knowledge base document.') : 'Selected knowledge base document to update.'}
                                  </p>
                                </div>
                              ) : (
                                <p style={{ margin: 0, fontSize: '13px', color: '#a1a1aa' }}>
                                  {gap?.gap_type === 'MISSING_DOCUMENT' ? '✨ Recommend creating a new standalone guide.' : 'No document selected. Pick a document below or create a new one.'}
                                </p>
                              )}

                              {/* Searchable Document Dropdown Picker */}
                              {showDocPicker[q.id] && (
                                <div style={{
                                  marginTop: '12px',
                                  padding: '10px',
                                  backgroundColor: 'rgba(9, 9, 11, 0.95)',
                                  borderRadius: '8px',
                                  border: '1px solid rgba(63, 63, 70, 0.6)',
                                  animation: 'slideUp 0.15s ease-out'
                                }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px', borderBottom: '1px solid rgba(63, 63, 70, 0.4)', paddingBottom: '6px' }}>
                                    <Search size={13} color="#71717a" />
                                    <input
                                      type="text"
                                      placeholder="Search existing documents..."
                                      value={targetDocSearchQuery[q.id] || ''}
                                      onChange={(e) => setTargetDocSearchQuery(prev => ({ ...prev, [q.id]: e.target.value }))}
                                      onClick={(e) => e.stopPropagation()}
                                      style={{
                                        background: 'transparent',
                                        border: 'none',
                                        outline: 'none',
                                        color: '#fafafa',
                                        fontSize: '12px',
                                        width: '100%'
                                      }}
                                      autoFocus
                                    />
                                    {targetDocSearchQuery[q.id] && (
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setTargetDocSearchQuery(prev => ({ ...prev, [q.id]: '' }));
                                        }}
                                        style={{ background: 'transparent', border: 'none', color: '#71717a', cursor: 'pointer' }}
                                      >
                                        <X size={12} />
                                      </button>
                                    )}
                                  </div>

                                  <div style={{ maxHeight: '160px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '3px' }}>
                                    {groupedDocuments
                                      .filter(doc => !targetDocSearchQuery[q.id] || doc.filename.toLowerCase().includes(targetDocSearchQuery[q.id].toLowerCase()))
                                      .map((doc, idx) => (
                                        <div
                                          key={idx}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setSelectedTargetDocs(prev => ({ ...prev, [q.id]: doc.filename }));
                                            setShowDocPicker(prev => ({ ...prev, [q.id]: false }));
                                          }}
                                          style={{
                                            padding: '6px 8px',
                                            borderRadius: '6px',
                                            cursor: 'pointer',
                                            fontSize: '12px',
                                            color: chosenTargetDoc === doc.filename ? '#38bdf8' : '#e4e4e7',
                                            backgroundColor: chosenTargetDoc === doc.filename ? 'rgba(56, 189, 248, 0.15)' : 'transparent',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            transition: 'background 0.15s'
                                          }}
                                        >
                                          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>📄 {doc.filename}</span>
                                          {chosenTargetDoc === doc.filename && <Check size={13} color="#38bdf8" />}
                                        </div>
                                      ))}

                                    {groupedDocuments.filter(doc => !targetDocSearchQuery[q.id] || doc.filename.toLowerCase().includes(targetDocSearchQuery[q.id].toLowerCase())).length === 0 && (
                                      <span style={{ fontSize: '12px', color: '#71717a', padding: '8px' }}>No matching documents found</span>
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>

                            {/* Recommended Solution Card */}
                            <div style={{
                              backgroundColor: 'rgba(255, 255, 255, 0.03)',
                              border: '1px solid rgba(255, 255, 255, 0.06)',
                              borderRadius: '12px',
                              padding: '16px',
                            }}>
                              <span style={{ fontSize: '11px', fontWeight: '600', color: '#71717a', textTransform: 'uppercase', letterSpacing: '0.04em', display: 'block', marginBottom: '6px' }}>
                                Recommended Solution
                              </span>
                              <p style={{ margin: '0 0 4px 0', fontSize: '14px', fontWeight: '600', color: '#22c55e' }}>
                                ⚡ {gap?.suggested_action ? gap.suggested_action.replace(/_/g, ' ') : 'Review & Add Knowledge'}
                              </p>
                              <p style={{ margin: 0, fontSize: '12px', color: '#a1a1aa', lineHeight: '1.4' }}>
                                {gap?.gap_type === 'MISSING_SECTION' && 'Append the missing procedural steps to the target document.'}
                                {gap?.gap_type === 'MISSING_DOCUMENT' && 'Create a new standalone guide with the proposed outline.'}
                                {gap?.gap_type === 'TERMINOLOGY_GAP' && 'Add alias / synonym tags to help users find the existing document.'}
                                {gap?.gap_type === 'DEPTH_GAP' && 'Expand the existing doc with step-by-step practical instructions.'}
                                {gap?.gap_type === 'EDGE_CASE' && 'Document this specific scenario in the troubleshooting section.'}
                                {gap?.gap_type === 'PERMISSIONS_GAP' && 'Clarify the required user permissions and access prerequisites.'}
                                {(!gap?.gap_type || gap?.gap_type === 'RETRIEVAL_FAILURE' || gap?.gap_type === 'MANUAL_REVIEW') && 'Review knowledge base contents and update as appropriate.'}
                              </p>
                            </div>
                          </div>

                          {/* Starter Draft Structure Preview */}
                          {gap?.ai_drafted_content && answeringQuestion !== q.id && (
                            <div style={{
                              backgroundColor: 'rgba(34, 197, 94, 0.04)',
                              border: '1px solid rgba(34, 197, 94, 0.2)',
                              borderRadius: '12px',
                              padding: '16px',
                            }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                                <Sparkles size={15} style={{ color: '#22c55e' }} />
                                <span style={{ fontSize: '13px', fontWeight: '600', color: '#22c55e' }}>
                                  I've started a draft structure for you — review and add to it below:
                                </span>
                              </div>

                              <div style={{
                                backgroundColor: 'rgba(9, 9, 11, 0.8)',
                                border: '1px solid rgba(63, 63, 70, 0.4)',
                                borderRadius: '8px',
                                padding: '14px',
                                fontSize: '13px',
                                lineHeight: '1.6',
                                color: '#e4e4e7',
                                whiteSpace: 'pre-wrap',
                                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                                maxHeight: '180px',
                                overflowY: 'auto'
                              }}>
                                {gap.ai_drafted_content}
                              </div>
                            </div>
                          )}

                          {/* Answer / Ingestion Form (Phase 3: Mode A Create New vs Mode B Section-Aware Update) */}
                          {answeringQuestion === q.id ? (
                            <div style={{
                              display: 'flex',
                              flexDirection: 'column',
                              gap: '16px',
                              backgroundColor: 'rgba(24, 24, 27, 0.95)',
                              border: '1px solid rgba(34, 197, 94, 0.4)',
                              borderRadius: '14px',
                              padding: '20px',
                              boxShadow: '0 12px 36px rgba(0, 0, 0, 0.4)',
                            }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px', borderBottom: '1px solid rgba(255, 255, 255, 0.08)', paddingBottom: '12px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                  <Edit3 size={16} style={{ color: '#22c55e' }} />
                                  <span style={{ fontSize: '15px', fontWeight: '600', color: '#fafafa' }}>
                                    Knowledge Resolution & Ingestion
                                  </span>
                                </div>

                                {/* Mode Switcher Tabs */}
                                <div style={{ display: 'flex', gap: '6px', backgroundColor: 'rgba(9, 9, 11, 0.8)', padding: '3px', borderRadius: '10px', border: '1px solid rgba(63, 63, 70, 0.4)', flexWrap: 'wrap' }}>
                                  {gap?.suggested_target_document?.title && (
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setResolutionMode('UPDATE_EXISTING');
                                        const docName = gap.suggested_target_document.title;
                                        setTargetDocFilename(docName);
                                        setSelectedTargetDocs(prev => ({ ...prev, [q.id]: docName }));
                                        loadExistingDoc(docName, gap?.ai_drafted_content || '');
                                      }}
                                      style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '6px',
                                        padding: '6px 12px',
                                        borderRadius: '8px',
                                        fontSize: '12px',
                                        fontWeight: '600',
                                        cursor: 'pointer',
                                        border: 'none',
                                        backgroundColor: resolutionMode === 'UPDATE_EXISTING' && (targetDocFilename || chosenTargetDoc) === gap.suggested_target_document.title ? '#22c55e' : 'transparent',
                                        color: resolutionMode === 'UPDATE_EXISTING' && (targetDocFilename || chosenTargetDoc) === gap.suggested_target_document.title ? '#000000' : '#a1a1aa',
                                      }}
                                    >
                                      <FileText size={13} />
                                      Edit Suggested Doc
                                    </button>
                                  )}

                                  <button
                                    type="button"
                                    onClick={() => {
                                      setResolutionMode('UPDATE_EXISTING');
                                      setShowDocPicker(prev => ({ ...prev, [q.id]: true }));
                                    }}
                                    style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '6px',
                                      padding: '6px 12px',
                                      borderRadius: '8px',
                                      fontSize: '12px',
                                      fontWeight: '600',
                                      cursor: 'pointer',
                                      border: 'none',
                                      backgroundColor: resolutionMode === 'UPDATE_EXISTING' && (targetDocFilename || chosenTargetDoc) !== gap?.suggested_target_document?.title ? '#38bdf8' : 'transparent',
                                      color: resolutionMode === 'UPDATE_EXISTING' && (targetDocFilename || chosenTargetDoc) !== gap?.suggested_target_document?.title ? '#000000' : '#a1a1aa',
                                    }}
                                  >
                                    <Search size={13} />
                                    Choose Different Doc
                                  </button>

                                  <button
                                    type="button"
                                    onClick={() => {
                                      setResolutionMode('CREATE_NEW');
                                      const resolvedQ = cleanQuestion || gap?.resolved_question || q.question;
                                      setAnswerFilename(resolvedQ.replace(/\.txt$/i, '').trim());
                                      setAnswerText(gap?.ai_drafted_content ? `# ${resolvedQ}\n\n${gap.ai_drafted_content}` : `# ${resolvedQ}\n\n`);
                                    }}
                                    style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '6px',
                                      padding: '6px 12px',
                                      borderRadius: '8px',
                                      fontSize: '12px',
                                      fontWeight: '600',
                                      cursor: 'pointer',
                                      border: 'none',
                                      backgroundColor: resolutionMode === 'CREATE_NEW' ? '#eab308' : 'transparent',
                                      color: resolutionMode === 'CREATE_NEW' ? '#000000' : '#a1a1aa',
                                    }}
                                  >
                                    <PlusCircle size={13} />
                                    Add New Doc
                                  </button>
                                </div>
                              </div>

                              {/* Form Header Fields */}
                              <div style={{ display: 'flex', gap: '14px', flexWrap: 'wrap' }}>
                                <div style={{ flex: 1, minWidth: '260px' }}>
                                  <label style={{ display: 'block', fontSize: '12px', color: '#a1a1aa', marginBottom: '4px', fontWeight: '500' }}>
                                    {resolutionMode === 'UPDATE_EXISTING' ? 'Target Document to Update:' : 'New Document Title / Filename:'}
                                  </label>
                                  {resolutionMode === 'UPDATE_EXISTING' ? (
                                    <div style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'space-between',
                                      padding: '10px 14px',
                                      backgroundColor: '#09090b',
                                      border: '1px solid rgba(56, 189, 248, 0.4)',
                                      borderRadius: '8px',
                                      color: '#38bdf8',
                                      fontWeight: '600',
                                      fontSize: '13px'
                                    }}>
                                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        📄 {targetDocFilename || chosenTargetDoc}
                                      </span>
                                      <button
                                        type="button"
                                        onClick={() => setShowDocPicker(prev => ({ ...prev, [q.id]: !prev[q.id] }))}
                                        style={{
                                          background: 'rgba(56, 189, 248, 0.15)',
                                          border: '1px solid rgba(56, 189, 248, 0.3)',
                                          borderRadius: '6px',
                                          color: '#38bdf8',
                                          padding: '3px 8px',
                                          fontSize: '11px',
                                          cursor: 'pointer',
                                          flexShrink: 0
                                        }}
                                      >
                                        Change
                                      </button>
                                    </div>
                                  ) : (
                                    <input
                                      type="text"
                                      placeholder="e.g. How to configure dual currency pricing"
                                      value={answerFilename}
                                      onChange={(e) => setAnswerFilename(e.target.value)}
                                      style={{
                                        width: '100%',
                                        backgroundColor: '#09090b',
                                        border: '1px solid rgba(63, 63, 70, 0.5)',
                                        borderRadius: '8px',
                                        padding: '10px 12px',
                                        color: '#fafafa',
                                        fontSize: '13px',
                                      }}
                                    />
                                  )}
                                </div>

                                <div style={{ flex: 1, minWidth: '260px' }}>
                                  <label style={{ display: 'block', fontSize: '12px', color: '#a1a1aa', marginBottom: '4px', fontWeight: '500' }}>
                                    Resolved Topic / Question:
                                  </label>
                                  <input
                                    type="text"
                                    value={cleanQuestion}
                                    onChange={(e) => setCleanQuestion(e.target.value)}
                                    style={{
                                      width: '100%',
                                      backgroundColor: '#09090b',
                                      border: '1px solid rgba(63, 63, 70, 0.5)',
                                      borderRadius: '8px',
                                      padding: '10px 12px',
                                      color: '#eab308',
                                      fontSize: '13px',
                                      fontWeight: '500'
                                    }}
                                  />
                                </div>
                              </div>

                              {/* Full Document In-Place Editor */}
                              <div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                                  <div>
                                    <label style={{ fontSize: '13px', color: '#fafafa', fontWeight: '600', display: 'block' }}>
                                      {resolutionMode === 'UPDATE_EXISTING' ? 'Full Document In-Place Editor (Markdown):' : 'Document Content (Markdown):'}
                                    </label>
                                    <span style={{ fontSize: '11px', color: '#71717a' }}>
                                      {resolutionMode === 'UPDATE_EXISTING'
                                        ? 'Edit any part of the document or add new instructions. The full document will be re-chunked and re-indexed.'
                                        : 'Write the complete content for this new guide.'}
                                    </span>
                                  </div>
                                  {isFetchingDoc && (
                                    <span style={{ fontSize: '12px', color: '#38bdf8', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                      <RefreshCw size={12} className="spin" /> Loading document...
                                    </span>
                                  )}
                                </div>

                                <textarea
                                  placeholder="Full document content in Markdown..."
                                  value={answerText}
                                  onChange={(e) => setAnswerText(e.target.value)}
                                  rows={16}
                                  style={{
                                    width: '100%',
                                    backgroundColor: '#09090b',
                                    border: '1px solid rgba(63, 63, 70, 0.6)',
                                    borderRadius: '8px',
                                    padding: '14px',
                                    color: '#fafafa',
                                    fontSize: '13px',
                                    resize: 'vertical',
                                    lineHeight: '1.6',
                                    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace'
                                  }}
                                />
                              </div>

                              {/* Save & Cancel Buttons */}
                              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginTop: '4px' }}>
                                <button
                                  onClick={() => handleAnswerQuestion(q.id)}
                                  disabled={loading || isFetchingDoc}
                                  style={{
                                    flex: 2,
                                    backgroundColor: loading ? 'rgba(63, 63, 70, 0.5)' : '#22c55e',
                                    color: loading ? '#71717a' : '#000000',
                                    padding: '12px 18px',
                                    borderRadius: '8px',
                                    border: 'none',
                                    cursor: loading ? 'not-allowed' : 'pointer',
                                    fontSize: '13px',
                                    fontWeight: '600',
                                    transition: 'all 0.2s',
                                  }}
                                >
                                  {loading ? 'Re-chunking & Ingesting...' : (resolutionMode === 'UPDATE_EXISTING' ? `✓ Save & Ingest "${targetDocFilename || chosenTargetDoc}"` : '✓ Save & Create New Document')}
                                </button>
                                <button
                                  onClick={() => {
                                    setAnsweringQuestion(null);
                                    setAnswerText('');
                                    setAnswerFilename('');
                                    setCleanQuestion('');
                                  }}
                                  style={{
                                    flex: 1,
                                    backgroundColor: 'rgba(39, 39, 42, 0.5)',
                                    color: '#a1a1aa',
                                    padding: '12px 18px',
                                    borderRadius: '8px',
                                    border: '1px solid rgba(63, 63, 70, 0.5)',
                                    cursor: 'pointer',
                                    fontSize: '13px',
                                    fontWeight: '500',
                                    transition: 'all 0.2s',
                                  }}
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          ) : (
                            /* Primary Action Controls Bar */
                            <div style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '10px',
                              flexWrap: 'wrap',
                              borderTop: '1px solid rgba(255, 255, 255, 0.06)',
                              paddingTop: '14px'
                            }}>
                              {chosenTargetDoc ? (
                                <>
                                  <button
                                    onClick={() => {
                                      setAnsweringQuestion(q.id);
                                      setResolutionMode('UPDATE_EXISTING');
                                      const resolvedQ = cleanQuestion || gap?.resolved_question || q.question;
                                      setCleanQuestion(resolvedQ);
                                      setTargetDocFilename(chosenTargetDoc);
                                      loadExistingDoc(chosenTargetDoc, gap?.ai_drafted_content || '');
                                    }}
                                    style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '6px',
                                      backgroundColor: '#22c55e',
                                      color: '#000000',
                                      padding: '9px 16px',
                                      borderRadius: '8px',
                                      border: 'none',
                                      cursor: 'pointer',
                                      fontSize: '13px',
                                      fontWeight: '600',
                                      transition: 'all 0.2s',
                                    }}
                                  >
                                    <Edit3 size={14} />
                                    Edit & Update "{chosenTargetDoc}"
                                  </button>

                                  <button
                                    onClick={() => {
                                      setAnsweringQuestion(q.id);
                                      setResolutionMode('CREATE_NEW');
                                      const resolvedQ = cleanQuestion || gap?.resolved_question || q.question;
                                      setCleanQuestion(resolvedQ);
                                      setAnswerFilename(resolvedQ.replace(/\.txt$/i, '').trim());
                                      setAnswerText(gap?.ai_drafted_content ? `# ${resolvedQ}\n\n${gap.ai_drafted_content}` : `# ${resolvedQ}\n\n`);
                                    }}
                                    style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '6px',
                                      backgroundColor: 'rgba(39, 39, 42, 0.6)',
                                      color: '#e4e4e7',
                                      padding: '9px 14px',
                                      borderRadius: '8px',
                                      border: '1px solid rgba(63, 63, 70, 0.5)',
                                      cursor: 'pointer',
                                      fontSize: '13px',
                                      fontWeight: '500',
                                      transition: 'all 0.2s',
                                    }}
                                  >
                                    <FileText size={14} />
                                    Create as New Document Instead
                                  </button>
                                </>
                              ) : (
                                <button
                                  onClick={() => {
                                    setAnsweringQuestion(q.id);
                                    setResolutionMode('CREATE_NEW');
                                    const resolvedQ = gap?.resolved_question || q.question;
                                    setCleanQuestion(resolvedQ);
                                    setAnswerFilename(resolvedQ.replace(/\.txt$/i, '').trim());
                                    setAnswerText(gap?.ai_drafted_content || '');
                                  }}
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    backgroundColor: '#22c55e',
                                    color: '#000000',
                                    padding: '9px 16px',
                                    borderRadius: '8px',
                                    border: 'none',
                                    cursor: 'pointer',
                                    fontSize: '13px',
                                    fontWeight: '600',
                                    transition: 'all 0.2s',
                                  }}
                                >
                                  <Edit3 size={14} />
                                  Create Knowledge Document
                                </button>
                              )}

                              <button
                                onClick={() => handleDismissQuestion(q.id)}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '6px',
                                  backgroundColor: 'transparent',
                                  color: '#71717a',
                                  padding: '9px 14px',
                                  borderRadius: '8px',
                                  border: '1px solid rgba(63, 63, 70, 0.3)',
                                  cursor: 'pointer',
                                  fontSize: '13px',
                                  fontWeight: '500',
                                  marginLeft: 'auto',
                                  transition: 'all 0.2s',
                                }}
                              >
                                <X size={14} />
                                Dismiss
                              </button>
                            </div>
                          )}
                        </div>
                        );
                      })()}
                    </div>
                  );
                })}

              {filteredQuestions.length === 0 && (
                <div style={{
                  textAlign: 'center',
                  padding: '80px 16px',
                  color: '#71717a',
                  backgroundColor: 'rgba(34, 197, 94, 0.03)',
                  borderRadius: '16px',
                  border: '1px dashed rgba(34, 197, 94, 0.2)',
                }}>
                  <CheckCircle size={44} style={{ margin: '0 auto 16px', color: '#22c55e', opacity: 0.6 }} />
                  <p style={{ margin: '0 0 4px 0', fontWeight: '500', color: '#fafafa', fontSize: '15px' }}>
                    {unansweredFilter === 'flagged' ? 'No flagged responses' : unansweredFilter === 'unanswered' ? 'No unanswered escalations' : 'All items resolved'}
                  </p>
                  <p style={{ margin: 0, fontSize: '13px', color: '#71717a' }}>
                    There are no pending items in this view.
                  </p>
                </div>
              )}
            </div>
          </div>
          );
        })()}
      </div>

      {/* Session Viewer Transcript Modal */}
      {sessionModalOpen && (
        <div
          onClick={() => setSessionModalOpen(false)}
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.75)',
            backdropFilter: 'blur(8px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: '20px',
            animation: 'fadeIn 0.2s ease-out'
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: 'rgba(24, 24, 27, 0.98)',
              border: '1px solid rgba(255, 255, 255, 0.12)',
              borderRadius: '20px',
              width: '100%',
              maxWidth: '780px',
              maxHeight: '85vh',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 24px 64px rgba(0, 0, 0, 0.6)',
              overflow: 'hidden',
              animation: 'scaleUp 0.2s ease-out'
            }}
          >
            {/* Modal Header */}
            <div style={{
              padding: '20px 24px',
              borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              backgroundColor: 'rgba(9, 9, 11, 0.6)'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{ background: 'linear-gradient(135deg, #3b82f6, #8b5cf6)', padding: '10px', borderRadius: '12px' }}>
                  <MessageSquare size={18} color="#ffffff" />
                </div>
                <div>
                  <h3 style={{ margin: 0, fontSize: '17px', fontWeight: '600', color: '#fafafa' }}>
                    Conversation Transcript
                  </h3>
                  <span style={{ fontSize: '12px', color: '#a1a1aa' }}>
                    Session: <code style={{ color: '#38bdf8' }}>{sessionModalData?.session_id ? sessionModalData.session_id.substring(0, 12) + '...' : 'Live Context'}</code>
                  </span>
                </div>
              </div>

              <button
                onClick={() => setSessionModalOpen(false)}
                style={{
                  backgroundColor: 'rgba(255, 255, 255, 0.06)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: '8px',
                  color: '#a1a1aa',
                  padding: '8px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.15s'
                }}
              >
                <X size={16} />
              </button>
            </div>

            {/* Modal Scrollable Body */}
            <div style={{
              padding: '24px',
              overflowY: 'auto',
              display: 'flex',
              flexDirection: 'column',
              gap: '20px',
              flex: 1
            }}>
              {loadingSessionModal ? (
                <div style={{ textAlign: 'center', padding: '60px 0', color: '#71717a' }}>
                  <RefreshCw size={28} className="spin" style={{ margin: '0 auto 12px', color: '#3b82f6' }} />
                  <p style={{ margin: 0, fontSize: '14px' }}>Loading conversation history...</p>
                </div>
              ) : sessionModalData?.messages && sessionModalData.messages.length > 0 ? (
                sessionModalData.messages.map((msg, idx) => (
                  <div
                    key={idx}
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '14px',
                      backgroundColor: 'rgba(39, 39, 42, 0.35)',
                      border: '1px solid rgba(255, 255, 255, 0.06)',
                      borderRadius: '14px',
                      padding: '18px'
                    }}
                  >
                    {/* User Question */}
                    <div style={{ display: 'flex', gap: '12px' }}>
                      <div style={{
                        background: '#3b82f6',
                        width: '26px',
                        height: '26px',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        marginTop: '2px'
                      }}>
                        <span style={{ color: '#fff', fontSize: '12px', fontWeight: 'bold' }}>Q</span>
                      </div>
                      <div style={{ flex: 1 }}>
                        <p style={{ margin: 0, color: '#fafafa', fontSize: '14px', fontWeight: '600', lineHeight: '1.5' }}>
                          {msg.question}
                        </p>
                        {msg.created_at && (
                          <span style={{ fontSize: '11px', color: '#71717a', display: 'block', marginTop: '4px' }}>
                            {new Date(msg.created_at).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Sage Answer */}
                    <div style={{ display: 'flex', gap: '12px' }}>
                      <div style={{
                        background: '#8b5cf6',
                        width: '26px',
                        height: '26px',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        marginTop: '2px'
                      }}>
                        <span style={{ color: '#fff', fontSize: '12px', fontWeight: 'bold' }}>A</span>
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{
                          color: '#e4e4e7',
                          fontSize: '13px',
                          lineHeight: '1.6',
                          whiteSpace: 'pre-wrap',
                          marginBottom: '12px'
                        }}>
                          {msg.answer}
                        </div>

                        {/* Metadata & Source Pills */}
                        <div style={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: '8px',
                          alignItems: 'center',
                          backgroundColor: 'rgba(0, 0, 0, 0.3)',
                          padding: '10px 12px',
                          borderRadius: '8px',
                          border: '1px solid rgba(255, 255, 255, 0.04)'
                        }}>
                          {msg.retrieval_confidence && <Badge type={msg.retrieval_confidence} />}
                          {msg.top_similarity && (
                            <span style={{ color: '#71717a', fontSize: '11px' }}>
                              Sim: {Number(msg.top_similarity).toFixed(3)}
                            </span>
                          )}
                          {msg.sources && msg.sources.length > 0 && (
                            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginLeft: '6px', borderLeft: '1px solid rgba(255, 255, 255, 0.1)', paddingLeft: '10px' }}>
                              {msg.sources.map((s, sIdx) => {
                                const sName = typeof s === 'string' ? (s.startsWith('{') ? JSON.parse(s).name : s) : (s?.name || '');
                                if (!sName) return null;
                                return (
                                  <span
                                    key={sIdx}
                                    style={{
                                      backgroundColor: 'rgba(139, 92, 246, 0.12)',
                                      border: '1px solid rgba(139, 92, 246, 0.25)',
                                      color: '#a78bfa',
                                      fontSize: '11px',
                                      padding: '2px 8px',
                                      borderRadius: '12px'
                                    }}
                                  >
                                    📄 {sName}
                                  </span>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div style={{ textAlign: 'center', padding: '60px 0', color: '#71717a' }}>
                  <MessageSquare size={36} style={{ margin: '0 auto 12px', opacity: 0.4 }} />
                  <p style={{ margin: 0, fontSize: '14px' }}>No recorded chat logs found for this session ID.</p>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div style={{
              padding: '16px 24px',
              borderTop: '1px solid rgba(255, 255, 255, 0.08)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              backgroundColor: 'rgba(9, 9, 11, 0.6)'
            }}>
              <button
                onClick={() => {
                  setSessionModalOpen(false);
                  setActiveTab('dashboard');
                  if (sessionModalData?.session_id) {
                    setSearchQuery(sessionModalData.session_id);
                  }
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  backgroundColor: 'transparent',
                  border: 'none',
                  color: '#60a5fa',
                  fontSize: '12px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                <ExternalLink size={13} /> Open in Analytics Dashboard
              </button>

              <button
                onClick={() => setSessionModalOpen(false)}
                style={{
                  backgroundColor: 'rgba(255, 255, 255, 0.08)',
                  border: '1px solid rgba(255, 255, 255, 0.12)',
                  color: '#fafafa',
                  padding: '8px 18px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: '500'
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}